import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatToDecimals } from '@lib/helpers/formatting';
import ProjectDossierStream from 'components/projectDossier/ProjectDossierStream';
import ProjectDossierWp from 'components/projectDossier/ProjectDossierWp';
// import DashboardWheelsSection from 'components/charts/DashboardWheelsSection';
import { PersonCircle, InfoCircleFill, IcoEmptyCalendar, IcoNewAnalysis, IcoFolder, ArrowRight } from '@icons';
import { OETrans } from 'components/translation/OETrans';
import { translate } from 'components/translation/translation';
import { WithPermission } from '@lib/helpers/WithPermission';
import LandingCharts from '@components/charts/LandingCharts';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProjectsStats, getProjectStats } from '@lib/api_ts/statsApi';

const TriggerTooltip = ({ parentRef, textValue }) => {
	const [overflowActive, setOverflowActive] = useState(false);
	//const [style, setStyle] = useState(null);
	const childRef = useRef(null);
	const childRefInnerHTML = childRef.current?.innerHTML;
	useEffect(() => {
		if (parentRef.current && childRef.current && childRefInnerHTML) {
			//console.log('childRef offsetWidth', childRef.current.offsetWidth);
			//console.log('parentRef offsetWidth', parentRef.current.offsetWidth);

			setOverflowActive(parentRef.current.offsetWidth < childRef.current.offsetWidth);
			//setStyle({parent: parentRef.current.offsetWidth, child: childRef.current.offsetWidth})
		}
	}, [childRef, childRefInnerHTML, parentRef]);

	const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

	return (
		<ConditionalWrapper
			condition={overflowActive}
			wrapper={(children) => (
				<OverlayTrigger placement="top-start" overlay={<Tooltip className="">{textValue}</Tooltip>}>
					{children}
				</OverlayTrigger>
			)}
		>
			<span ref={childRef}>{textValue}</span>
		</ConditionalWrapper>
	);
};

const Summary = ({
	project,
	// summaryWheelsValues,
	//  setSummaryWheelsValues
}) => {
	const { projectId } = useParams();

	const { data: projectStats /*, isPending: isPendingProjectStats*/ } = useQuery({
		queryKey: ['projects-stats', projectId],
		queryFn: () => (!projectId ? getProjectsStats() : getProjectStats(projectId)),
	});

	const [expanded, setExpanded] = useState(false);
	const ref1 = useRef(null);
	const ref2 = useRef(null);
	const ref3 = useRef(null);
	const ref4 = useRef(null);
	const ref5 = useRef(null);
	const ref6 = useRef(null);
	const ref7 = useRef(null);
	const ref8 = useRef(null);
	const ref9 = useRef(null);
	const ref10 = useRef(null);
	const ref11 = useRef(null);
	const ref12 = useRef(null);
	const descRef = useRef(null);

	const businessPlanContainerRef = useRef(null);
	const thisProject = project;
	const isWP = thisProject.streamConfigurationType === 'WP';

	const lineHeight = descRef.current ? parseFloat(getComputedStyle(descRef.current).lineHeight) : 0;
	const height = descRef.current ? descRef.current.scrollHeight : 0;
	const lines = lineHeight > 0 ? Math.round(height / lineHeight) : 0;

	const isLong = lines > 4;

	const finalizedAmount = projectStats?.finalAmount || 0;

	return (
		<>
			{!!projectStats && project.hasAssignedInvoices ? (
				<div
					style={{
						backgroundColor: 'var(--np-primary-color--light)',
						padding: '1.5rem',
						borderRadius: '12px',
						marginBottom: '3rem',
						display: 'flex',
						alignItems: 'center',
						gap: '0.5rem',
					}}
				>
					<PersonCircle
						style={{
							fill: 'var(--np-primary-color)',
							width: '26px',
							height: '26px',
						}}
					/>{' '}
					{projectStats.ownership.totalAssignedInvoices > 1 ? (
						<span>
							Sono presenti{' '}
							<span
								style={{
									fontWeight: 700,
								}}
							>
								{projectStats.ownership.totalAssignedInvoices} controlli assegnati a te
							</span>
						</span>
					) : projectStats.ownership.totalAssignedInvoices === 1 ? (
						<span>
							È presente{' '}
							<span
								style={{
									fontWeight: 700,
								}}
							>
								{projectStats.ownership.totalAssignedInvoices} controllo assegnato a te
							</span>
						</span>
					) : null}
					<Link
						to={`/project/${project.id}/summary`}
						className="btn custom-ghost-button"
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '.5rem',
							textTransform: 'none',
							marginLeft: 'auto',
						}}
					>
						vai <ArrowRight />
					</Link>
				</div>
			) : null}

			<WithPermission permission="view-dashboard-charts">
				<LandingCharts />
				{/* 
				<DashboardWheelsSection projectId={project.id} setSummaryWheelsValues={setSummaryWheelsValues} /> */}
			</WithPermission>

			<WithPermission permission="view-project-registry">
				<div className="simple-box scenaritab">
					<div className="row d-flex align-items-center mb-4">
						<div className="col-4 ps-0">
							<h5 className="title-box">
								<OETrans code="common.registry" fallback={'Anagrafica'} />
							</h5>
						</div>
					</div>

					<div id="registry-summary">
						<Row xs={2} className="g-5">
							{/* TITOLARI */}
							<Col>
								<Row className="row-title-registry">
									<Col className="icon-registry col-auto ps-0 pe-2 d-flex justify-content-center align-items-center">
										<PersonCircle />
									</Col>
									<Col className="title-registry">{translate('common.registry.owners', 'Titolari')}</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">
										{isWP ? translate('common.registry.coordinator', 'Coordinatore') : translate('common.registry.society', 'Società')}
									</Col>

									<Col className="value-detail-registry" ref={ref1}>
										<TriggerTooltip parentRef={ref1} textValue={thisProject.company?.name} />
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.projectResponsible', 'Responsabile di Progetto')}</Col>

									<Col className="value-detail-registry" ref={ref2}>
										<TriggerTooltip parentRef={ref2} textValue={thisProject.manager ?? <>N.D.</>} />
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.projectManager', 'Project Manager')}</Col>

									<Col className="value-detail-registry" ref={ref3}>
										<TriggerTooltip parentRef={ref3} textValue={thisProject.oeManager ?? <>N.D.</>} />
									</Col>
								</Row>

								{!isWP && (
									<Row className="row-detail-registry">
										<Col className="label-detail-registry">{translate('common.registry.localization', 'Localizzazione')}</Col>

										<Col className="value-detail-registry" ref={ref4}>
											<TriggerTooltip
												parentRef={ref4}
												textValue={thisProject.network ? thisProject.networkNote : (thisProject.location?.nomeComune ?? <>N.D.</>)}
											/>
										</Col>
									</Row>
								)}
							</Col>

							{/* DETTAGLI */}
							<Col>
								<Row className="row-title-registry">
									<Col className="icon-registry col-auto ps-0 pe-2">
										<InfoCircleFill />
									</Col>
									<Col className="title-registry">{translate('common.registry.details', 'Dettagli')}</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.fund', 'Fondo')}</Col>

									<Col className="value-detail-registry" ref={ref5}>
										<TriggerTooltip parentRef={ref5} textValue={thisProject?.notice?.fund?.name} />
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.notice', 'Bando')}</Col>

									<Col className="value-detail-registry" ref={ref6}>
										<TriggerTooltip parentRef={ref6} textValue={thisProject?.notice?.call} />
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.businessPlan', 'Incluso nel Business Plan')}</Col>

									<Col className="value-detail-registry" ref={businessPlanContainerRef}>
										<TriggerTooltip parentRef={businessPlanContainerRef} textValue={thisProject?.businessPlan ? 'Sì' : 'No'} />
									</Col>
								</Row>
							</Col>

							{/* TIMING */}
							<Col>
								<Row className="row-title-registry">
									<Col className="icon-registry col-auto ps-0 pe-2">
										<IcoEmptyCalendar />
									</Col>
									<Col className="title-registry">{translate('common.registry.timing', 'Timing')}</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.duration', 'Durata (mesi)')}</Col>

									<Col className="value-detail-registry" ref={ref10}>
										<TriggerTooltip parentRef={ref10} textValue={thisProject.durationInMonths} />
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.effectiveStart', 'Avvio effettivo')}</Col>

									<Col className="value-detail-registry" ref={ref11}>
										<TriggerTooltip
											parentRef={ref11}
											textValue={
												thisProject.startDate ? (
													new Date(thisProject.startDate).toLocaleDateString('en-GB')
												) : thisProject.forecastStartDate ? (
													new Date(thisProject.forecastStartDate).toLocaleDateString('en-GB')
												) : (
													<>N.D.</>
												)
											}
										/>
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.forecastEnd', 'Fine prevista')}</Col>

									<Col className="value-detail-registry" ref={ref12}>
										<TriggerTooltip
											parentRef={ref12}
											textValue={
												thisProject.endDate ? (
													new Date(thisProject.endDate).toLocaleDateString('en-GB')
												) : thisProject.forecastEndDate ? (
													new Date(thisProject.forecastEndDate).toLocaleDateString('en-GB')
												) : (
													<>N.D.</>
												)
											}
										/>
									</Col>
								</Row>
							</Col>

							{/* DIMENSIONE ECONOMICA */}
							<Col>
								<Row className="row-title-registry">
									<Col className="icon-registry col-auto ps-0 pe-2">
										<IcoNewAnalysis />
									</Col>
									<Col className="title-registry">{translate('common.registry.economicDimension', 'Dimensione economica')} (€)</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.projectCost', 'Costo progetto')}</Col>

									<Col className="value-detail-registry" ref={ref7}>
										<TriggerTooltip
											parentRef={ref7}
											textValue={
												formatToDecimals(
													projectStats?.totalAmount || 0,
													// summaryWheelsValues
													// 	.filter((el) => el.propertyName === 'totalAmount')
													// 	.map((el) => el.propertyValue ?? 0)
													// 	.pop(),
												) + ' €'
											}
										/>
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.grant', 'Contributo concesso')}</Col>

									<Col className="value-detail-registry" ref={ref8}>
										<TriggerTooltip
											parentRef={ref8}
											textValue={
												formatToDecimals(
													projectStats?.totalApprovedAmount || 0,
													// summaryWheelsValues
													// 	.filter((el) => el.propertyName === 'totalApprovedAmount')
													// 	.map((el) => el.propertyValue ?? 0)
													// 	.pop(),
												) + ' €'
											}
										/>
									</Col>
								</Row>

								<Row className="row-detail-registry">
									<Col className="label-detail-registry">{translate('common.registry.expense', 'Spesa effettuata')}</Col>

									<Col className="value-detail-registry" ref={ref9}>
										<TriggerTooltip parentRef={ref9} textValue={formatToDecimals(finalizedAmount) + ' €'} />
									</Col>
								</Row>
							</Col>

							{/* DESCRIZIONE */}
							{thisProject.description && (
								<Col className="col-12">
									<Row className="row-title-registry">
										<Col className="icon-registry col-auto ps-0 pe-2 d-flex justify-content-center align-items-center">
											<IcoFolder />
										</Col>
										<Col className="title-registry">{translate('common.registry.description', 'Descrizione')}</Col>
										{isLong && (
											<Col
												className="col-auto me-auto new-text-as-link"
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => setExpanded((prev) => !prev)}
											>
												leggi di {expanded ? 'meno' : 'più'}
												<span className={`svg-arrow${expanded ? ' rotated' : ''}`}></span>
											</Col>
										)}
									</Row>

									<Row className="row-detail-registry">
										<Col ref={descRef} className={`registry-description ${expanded ? 'expanded' : ''}`}>
											{thisProject.description}
										</Col>
									</Row>
								</Col>
							)}
						</Row>
					</div>
				</div>
			</WithPermission>

			<WithPermission permission="view-project-folder">
				<div className="simple-box scenaritab" style={{ marginTop: '2.5rem' }}>
					<div className="row d-flex align-items-center mb-4">
						<div className="col-4 ps-0">
							<h5 className="title-box">{translate('common.registry.projectDossier', 'Fascicolo di progetto')}</h5>
						</div>
					</div>

					<Row className="mt-3 px-3">
						{thisProject.streamConfigurationType === 'STREAM' ? (
							<ProjectDossierStream entityObj={thisProject} />
						) : (
							<ProjectDossierWp entityObj={thisProject} />
						)}
					</Row>
				</div>
			</WithPermission>
		</>
	);
};
export default Summary;
